/**
 * Collection Online Seminars Carousel
 * Handles horizontal scroll carousel with pagination dots on mobile
 */

(function () {
  'use strict';

  // Initialize all carousels on the page
  function initCarousels() {
    const carouselSections = document.querySelectorAll('.collection-online-seminars');

    carouselSections.forEach((section) => {
      const sectionId = section.getAttribute('data-section-id');
      if (!sectionId) {
        return;
      }

      // Get the scrollable container (parent with overflow-x)
      const scrollableContainer = section.querySelector('.collection-online-seminars__products-container');
      const productsContainer = section.querySelector(`#collection-online-seminars-products-${sectionId}`);
      const paginationContainer = section.querySelector(`#collection-online-seminars-pagination-${sectionId}`);

      if (!scrollableContainer) {
        return;
      }

      if (!productsContainer) {
        return;
      }

      const products = productsContainer.querySelectorAll('.collection-online-seminars__product-item');
      const totalProducts = products.length;

      if (totalProducts === 0) {
        return;
      }

      // Hide pagination if only one product
      if (totalProducts <= 1) {
        if (paginationContainer) {
          paginationContainer.style.display = 'none';
        }
        return;
      }

      // Only proceed if pagination container exists
      if (!paginationContainer) {
        return;
      }

      // Generate pagination dots
      generatePaginationDots(paginationContainer, totalProducts, scrollableContainer, products);

      // Set up scroll listener on the scrollable container
      setupScrollListener(scrollableContainer, paginationContainer, products);

      // Initial update of indicators
      const initialIndex = getCurrentProductIndex(scrollableContainer, products);
      updateActiveDot(paginationContainer, initialIndex);
    });
  }

  /**
   * Generate pagination dots
   */
  function generatePaginationDots(container, totalProducts, scrollableContainer, products) {
    container.innerHTML = '';

    for (let i = 0; i < totalProducts; i++) {
      const dot = document.createElement('button');
      dot.className = 'collection-online-seminars__pagination-dot';
      dot.setAttribute('data-index', i);
      dot.setAttribute('aria-label', `Go to product ${i + 1} of ${totalProducts}`);
      dot.setAttribute('role', 'tab');

      if (i === 0) {
        dot.classList.add('active');
        dot.setAttribute('aria-selected', 'true');
        dot.setAttribute('tabindex', '0');
      } else {
        dot.setAttribute('aria-selected', 'false');
        dot.setAttribute('tabindex', '-1');
      }

      // Click handler
      dot.addEventListener('click', function () {
        const targetIndex = parseInt(this.getAttribute('data-index'), 10);
        scrollToProduct(scrollableContainer, products, targetIndex);
        updateActiveDot(container, targetIndex);
      });

      // Keyboard navigation
      dot.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const targetIndex = parseInt(this.getAttribute('data-index'), 10);
          scrollToProduct(scrollableContainer, products, targetIndex);
          updateActiveDot(container, targetIndex);
        }
      });

      container.appendChild(dot);
    }
  }

  /**
   * Scroll to specific product
   */
  function scrollToProduct(container, products, index) {
    if (index < 0 || index >= products.length) return;

    const targetProduct = products[index];
    if (!targetProduct) return;

    // For scroll snap, we can use scrollIntoView or calculate scroll position
    // Using scrollIntoView with smooth behavior works well with scroll-snap
    targetProduct.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'start'
    });
  }

  /**
   * Update active dot based on scroll position
   */
  function updateActiveDot(container, activeIndex) {
    if (!container) {
      return;
    }

    const dots = container.querySelectorAll('.collection-online-seminars__pagination-dot');
    if (dots.length === 0) {
      return;
    }

    dots.forEach((dot, index) => {
      const isActive = index === activeIndex;
      dot.classList.toggle('active', isActive);
      dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      dot.setAttribute('tabindex', isActive ? '0' : '-1');
      dot.setAttribute('aria-current', isActive ? 'page' : 'false');
    });
  }

  /**
   * Determine which product is currently in view
   */
  function getCurrentProductIndex(container, products) {
    if (!container || products.length === 0) return 0;

    const containerRect = container.getBoundingClientRect();
    const scrollLeft = container.scrollLeft;
    const containerWidth = containerRect.width;

    // Calculate which product is most visible based on scroll position
    // Each product width is approximately: container width - gap (16px on each side = 32px total)
    const productWidth = containerWidth - 32; // Account for padding on sides
    const gap = 16;

    // Find the index of the product that's currently most visible
    let currentIndex = Math.round(scrollLeft / (productWidth + gap));

    // Ensure index is within bounds
    currentIndex = Math.max(0, Math.min(currentIndex, products.length - 1));

    // Alternative method: find product with most area visible
    let bestIndex = currentIndex;
    let maxVisibleArea = 0;

    products.forEach((product, index) => {
      const productRect = product.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      // Calculate intersection
      const left = Math.max(productRect.left, containerRect.left);
      const right = Math.min(productRect.right, containerRect.right);
      const visibleWidth = Math.max(0, right - left);

      if (visibleWidth > maxVisibleArea) {
        maxVisibleArea = visibleWidth;
        bestIndex = index;
      }
    });

    return bestIndex;
  }

  /**
   * Set up scroll listener with throttling
   */
  function setupScrollListener(container, paginationContainer, products) {
    if (!container) {
      return;
    }

    let scrollTicking = false;
    let lastKnownIndex = -1;

    const handleScroll = () => {
      if (!scrollTicking) {
        requestAnimationFrame(() => {
          const currentIndex = getCurrentProductIndex(container, products);

          // Always update on first call or when index changed
          if (lastKnownIndex === -1 || currentIndex !== lastKnownIndex) {
            lastKnownIndex = currentIndex;
            updateActiveDot(paginationContainer, currentIndex);
          }

          scrollTicking = false;
        });
        scrollTicking = true;
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });

    // Also listen for scroll end to ensure final state is correct
    let scrollTimeout;
    container.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const currentIndex = getCurrentProductIndex(container, products);
        updateActiveDot(paginationContainer, currentIndex);
      }, 150);
    }, { passive: true });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCarousels);
  } else {
    initCarousels();
  }
})();

